import { dailyBurn } from "./body";
import { todayStr } from "./date";
import { curWeight, dayKcal } from "./energy";
import { hasCondition, ironRule, older, proteinRule, type DeviceAnswers } from "./eating";
import { deficitTarget, foodBudget, proteinTarget, type IntakeTotals } from "./intake";
import type { KayaState } from "./types";

/**
 * What a day of eating should add up to, nutrient by nutrient. (ADR-026)
 *
 * Every target is `null` when Kaya cannot state it honestly — no body numbers means no energy
 * budget, and every figure worked out as a share of that budget goes with it; an RDA that differs
 * by sex is null until the sex is known. A screen shows "needs your body numbers" for those, not
 * a placeholder dressed as advice. (invariant 11)
 *
 * Sources:
 * - Energy: `intake.ts::foodBudget` — `dailyBurn` with Work credited at 0.7, less the deficit in
 *   force; the same budget as `energyBalance` and `dayLedger` (ADR-025, ADR-041).
 * - Protein: `proteinTarget`, g per kg of current weight (default 1.6 — training in a deficit;
 *   1.0–1.2 at 60+, held at 0.8 with kidney disease — `eating.ts`, ADR-039).
 * - Fat: 27.5 % of energy, the middle of the 25–30 % band in ICMR-NIN's Dietary Guidelines for
 *   Indians (2024) and WHO's ≤ 30 %.
 * - Saturated fat: under 10 % of energy (WHO 2023 guideline on saturated and trans fat).
 * - Carbohydrate: what is left of the budget after protein and fat, at 4 / 4 / 9 kcal per g.
 * - Fibre: 30 g men, 25 g women (ICMR-NIN 2020 suggests ~40 g per 2,000 kcal; 30/25 is the
 *   common adult floor that a deficit diet can actually reach).
 * - Sodium: under 2,000 mg (WHO 2012; ICMR-NIN 2020 agrees at 5 g of salt).
 * - Calcium: 1,000 mg (ICMR-NIN 2020 RDA, adults).
 * - Iron: 19 mg men, 29 mg women (ICMR-NIN 2020 RDA, sedentary adults; the women's figure is for
 *   the menstruating years, so from 50 or once periods have stopped it is 19 — `eating.ts`).
 * - Potassium: 3,500 mg (ICMR-NIN 2020 and WHO 2012, ≥ 3,510 mg).
 */

export const FAT_ENERGY_SHARE = 0.275;
export const SAT_FAT_ENERGY_MAX = 0.1;
export const SODIUM_MAX_MG = 2000;
export const CALCIUM_MG = 1000;
export const POTASSIUM_MG = 3500;
export const FIBRE_G = { male: 30, female: 25 } as const;
export { IRON_MG } from "./eating";

export type NutrientKey = "p" | "c" | "f" | "fb" | "sf" | "na" | "ca" | "fe" | "pot";

export interface DailyTargets {
  /** the day's calorie budget — Work credited at 0.7 (ADR-041) */
  kcal: number | null;
  /** grams — always known, because a weight always is */
  protein: number;
  /** grams a main meal should reach: 25 at 60+, 20 otherwise (ADR-039) */
  mealProtein: number;
  /** the rule behind a target where age or an answer changed it, in the row's own words */
  why: Partial<Record<NutrientKey, string>>;
  fat: number | null;
  /** a ceiling, not a goal */
  satFatMax: number | null;
  carbs: number | null;
  fibre: number | null;
  /** a ceiling, not a goal */
  sodiumMax: number;
  calcium: number;
  iron: number | null;
  potassium: number;
}

function sexOf(S: KayaState): "male" | "female" | null {
  const s = S.profile.sex;
  return s === "male" || s === "female" ? s : null;
}

/**
 * The targets for a day. `activeKcal` defaults to what Kaya measured on `today`'s date; pass 0
 * to plan a day that has not happened, whose sessions would only add to it.
 */
export function dailyTargets(
  S: KayaState,
  today = new Date(),
  activeKcal = Math.round(dayKcal(S, todayStr(today))),
  device?: DeviceAnswers
): DailyTargets {
  const burn = dailyBurn(S, curWeight(S), activeKcal, today);
  // The day the targets are for decides the deficit: none on a day marked as a fast (ADR-049).
  const kcal = burn ? foodBudget(burn.total, burn.active, deficitTarget(S, today, device, todayStr(today))) : null;
  const protein = proteinTarget(S, curWeight(S), today);
  const fat = kcal !== null ? Math.round((kcal * FAT_ENERGY_SHARE) / 9) : null;
  const satFatMax = kcal !== null ? Math.round((kcal * SAT_FAT_ENERGY_MAX) / 9) : null;
  const carbs =
    kcal !== null && fat !== null ? Math.max(0, Math.round((kcal - protein * 4 - fat * 9) / 4)) : null;
  const sex = sexOf(S);
  const pr = proteinRule(S, today);
  const iron = ironRule(S, today);
  const isOlder = older(S, today);

  const why: DailyTargets["why"] = {};
  if (pr.reason === "kidney") why.p = `${pr.perKg} g/kg — kidney disease; your doctor sets this`;
  else if (pr.reason === "glp1") why.p = `at least ${pr.perKg} g/kg on a GLP-1 or weight-loss medicine`;
  else if (pr.reason === "60+") why.p = `${pr.perKg} g/kg after 60, ${pr.mealG} g a meal`;
  if (isOlder && sex) why.fb = `${FIBRE_G[sex]} g for ${sex === "female" ? "women" : "men"}`;
  if (hasCondition(S, "bp")) why.na = "High BP";
  if (isOlder) {
    why.ca = "the adult figure";
    why.pot = "the adult figure";
  }
  if (iron.reason) why.fe = "the adult figure — 29 mg is for the menstruating years";

  return {
    kcal,
    protein,
    mealProtein: pr.mealG,
    why,
    fat,
    satFatMax,
    carbs,
    fibre: sex ? FIBRE_G[sex] : null,
    sodiumMax: SODIUM_MAX_MG,
    calcium: CALCIUM_MG,
    iron: iron.mg,
    potassium: POTASSIUM_MG,
  };
}

/** One row of the nutrient panel: what was eaten, against what, and which way is good. */
export interface NutrientRow {
  key: NutrientKey;
  label: string;
  unit: "g" | "mg";
  value: number;
  target: number | null;
  /** a ceiling reads the other way: filling it is the warning, not the goal */
  ceiling: boolean;
  /** why the target is what it is, in one line */
  why: string;
  /** true for the minerals, which older entries do not carry */
  micro: boolean;
}

export function nutrientRows(t: IntakeTotals, T: DailyTargets): NutrientRow[] {
  // A target that age or an answer changed says which rule set it; the rest keep the general line.
  return rowsOf(t, T).map((r) => ({ ...r, why: T.why[r.key] ?? r.why }));
}

function rowsOf(t: IntakeTotals, T: DailyTargets): NutrientRow[] {
  return [
    { key: "p", label: "Protein", unit: "g", value: t.p, target: T.protein, ceiling: false, micro: false,
      why: "g per kg of your weight — set under Food & calories" },
    { key: "c", label: "Carbohydrate", unit: "g", value: t.c, target: T.carbs, ceiling: false, micro: false,
      why: "what the budget leaves after protein and fat" },
    { key: "f", label: "Fat", unit: "g", value: t.f, target: T.fat, ceiling: false, micro: false,
      why: "27.5 % of the budget (ICMR-NIN 25–30 %)" },
    { key: "fb", label: "Fibre", unit: "g", value: t.fb, target: T.fibre, ceiling: false, micro: false,
      why: "30 g men, 25 g women" },
    { key: "sf", label: "Saturated fat", unit: "g", value: t.sf, target: T.satFatMax, ceiling: true, micro: true,
      why: "under 10 % of the budget (WHO)" },
    { key: "na", label: "Sodium", unit: "mg", value: t.na, target: T.sodiumMax, ceiling: true, micro: true,
      why: "under 2,000 mg — about 5 g of salt (WHO)" },
    { key: "ca", label: "Calcium", unit: "mg", value: t.ca, target: T.calcium, ceiling: false, micro: true,
      why: "1,000 mg (ICMR-NIN 2020)" },
    { key: "fe", label: "Iron", unit: "mg", value: t.fe, target: T.iron, ceiling: false, micro: true,
      why: "19 mg men, 29 mg women (ICMR-NIN 2020)" },
    { key: "pot", label: "Potassium", unit: "mg", value: t.pot, target: T.potassium, ceiling: false, micro: true,
      why: "3,500 mg (ICMR-NIN 2020, WHO)" },
  ];
}
